import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../components/AuthContext';
import { api } from '../api/client';

export default function Dashboard() {
  const { user } = useAuth();
  const [vehicles, setVehicles]   = useState([]);
  const [history, setHistory]     = useState([]);
  const [credits, setCredits]     = useState(user?.credits ?? 0);
  const [referral, setReferral]   = useState(null);
  const [copied, setCopied]       = useState(false);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    Promise.all([
      api.vehicles.list(),
      api.messages.history(),
      api.payments.credits(),
      api.auth.referral(),
    ])
      .then(([v, h, c, r]) => {
        setVehicles(v);
        setHistory(h);
        setCredits(c.credits);
        setReferral(r);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const copyLink = () => {
    if (!referral?.referral_link) return;
    navigator.clipboard.writeText(referral.referral_link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">{greeting}, {user?.firstName}!</h1>
        <p className="page-subtitle">Here's what's happening with your account</p>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{loading ? '—' : vehicles.length}</div>
          <div className="stat-label">Registered vehicles</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{loading ? '—' : history.length}</div>
          <div className="stat-label">Messages sent</div>
        </div>
        <div className="stat-card" style={{ borderTopColor: credits > 0 ? 'var(--accent)' : 'var(--danger)' }}>
          <div className="stat-value" style={{ color: credits > 0 ? 'var(--accent)' : 'var(--danger)' }}>
            {loading ? '—' : credits}
          </div>
          <div className="stat-label">Credits remaining</div>
        </div>
        <div className="stat-card" style={{ borderTopColor: '#52ff8a' }}>
          <div className="stat-value" style={{ color: '#52ff8a' }}>
            {loading ? '—' : referral?.referral_count ?? 0}
          </div>
          <div className="stat-label">Successful referrals</div>
        </div>
      </div>

      {/* Low credits warning */}
      {credits < 5 && !loading && (
        <div className="alert alert-error" style={{ marginBottom: 24 }}>
          You don't have enough credits to send a message.{' '}
          <Link to="/topup" style={{ color: 'var(--danger)', fontWeight: 700 }}>
            Top up now &rarr;
          </Link>
        </div>
      )}

      {/* Main grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>

        {/* Quick actions */}
        <div className="card">
          <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, fontFamily: 'Syne, sans-serif' }}>Quick actions</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Link to="/send" className="btn btn-primary" style={{ justifyContent: 'center', textDecoration: 'none' }}>
              Send a message
            </Link>
            <Link to="/topup" className="btn btn-secondary" style={{ justifyContent: 'center', textDecoration: 'none' }}>
              Top up credits
            </Link>
            <Link to="/vehicles" className="btn btn-secondary" style={{ justifyContent: 'center', textDecoration: 'none' }}>
              Register a vehicle
            </Link>
          </div>
        </div>

        {/* Recent messages */}
        <div className="card">
          <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, fontFamily: 'Syne, sans-serif' }}>Recent messages</h2>
          {loading && <p style={{ color: 'var(--muted)', fontSize: 14 }}>Loading...</p>}
          {!loading && history.length === 0 && (
            <p style={{ color: 'var(--muted)', fontSize: 14 }}>No messages sent yet.</p>
          )}
          {!loading && history.slice(0, 3).map(m => (
            <div key={m.id} style={{ borderBottom: '1px solid var(--border)', paddingBottom: 10, marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 13, color: 'var(--accent)', letterSpacing: 1 }}>
                  {m.sender_registration}
                </span>
                <span style={{ color: 'var(--muted)' }}>&rarr;</span>
                <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 13, color: 'var(--text)', letterSpacing: 1 }}>
                  {m.recipient_registration}
                </span>
              </div>
              <div style={{ fontSize: 13, color: 'var(--muted)' }}>
                {m.message_body.substring(0, 60)}{m.message_body.length > 60 ? '...' : ''}
              </div>
            </div>
          ))}
          {!loading && history.length > 0 && (
            <Link to="/history" style={{ color: 'var(--accent)', fontSize: 13, textDecoration: 'none' }}>
              View all &rarr;
            </Link>
          )}
        </div>
      </div>

      {/* Referral card — full width */}
      <div className="card" style={{ borderTop: '3px solid #52ff8a' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 6, fontFamily: 'Syne, sans-serif', color: '#52ff8a' }}>
              &#127881; Refer a friend
            </h2>
            <p style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.6, marginBottom: 0 }}>
              Share your link and you'll both get <strong style={{ color: 'var(--text)' }}>50 credits</strong> when they verify their email.
              You've made <strong style={{ color: '#52ff8a' }}>{referral?.referral_count ?? 0}</strong> successful referral{referral?.referral_count !== 1 ? 's' : ''} so far.
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 260 }}>
            {/* Link display */}
            <div style={{
              background: 'var(--surface2)', border: '1px solid var(--border)',
              borderRadius: 8, padding: '10px 14px',
              fontFamily: 'monospace', fontSize: 13, color: 'var(--muted)',
              wordBreak: 'break-all', lineHeight: 1.5,
            }}>
              {loading ? 'Loading...' : referral?.referral_link ?? '—'}
            </div>
            {/* Copy button */}
            <button onClick={copyLink} style={{
              background: copied ? 'rgba(82,255,138,0.15)' : 'var(--accent-bg)',
              border: `1px solid ${copied ? 'rgba(82,255,138,0.4)' : 'var(--accent-border)'}`,
              borderRadius: 8, color: copied ? '#52ff8a' : 'var(--accent)',
              cursor: 'pointer', fontFamily: 'Syne, sans-serif', fontWeight: 700,
              fontSize: 13, padding: '10px 16px', transition: 'all 0.2s',
            }}>
              {copied ? '&#10003; Copied!' : 'Copy referral link'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
